import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Splash } from '@/components/splash';
import { useAuth } from '@/lib/auth/auth-context';
import { rememberReturnTo } from '@/lib/auth/return-to';
import type { AuthAudience } from '@/lib/auth/types';

/**
 * Gate for authenticated areas.
 *
 * Unauthenticated visitors go to `/login` (password form, demo accounts, and an
 * SSO button). We do not auto-redirect to the identity provider: that hides the
 * testing accounts and 502s if the API is still coming up.
 *
 * While the initial silent auth resolves, `status` is `unknown` — show the
 * splash, never redirect. A signed-in user returning to the app must not be
 * bounced to /login because we asked too early.
 *
 * The intended path is parked BEFORE leaving, because a later SSO round-trip is
 * a full page load that wipes React state.
 */
export function ProtectedRoute({ children }: { children: ReactNode; audience?: AuthAudience }) {
  const { status, mode } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (status !== 'unauthenticated') return;
    rememberReturnTo(`${location.pathname}${location.search}${location.hash}`);
  }, [status, location.pathname, location.search, location.hash]);

  if (status === 'unknown' || mode === null) return <Splash />;
  if (status === 'authenticated') return <>{children}</>;
  return <Navigate to="/login" replace />;
}
